import { useMemo, useState } from "react";
import { Button, Modal, Select, Space } from "antd";

import { EMPTY_SERIES_FIELDS, accountKey, sourceKeyOf, toId } from "./reportingUtils";

const Label = ({ children }) => <div style={{ fontWeight: 600, margin: "16px 0 6px" }}>{children}</div>;

const filterByLabel = (input, option) =>
  String(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

const sourceFields = (value) => {
  const [kind, id] = value.split(":");
  return kind === "bc"
    ? { business_case_id: Number(id), scenario_id: -1 }
    : { scenario_id: Number(id), business_case_id: -1 };
};

// Adds one series per selected account, all from the same source and tab.
// hideSource: custom-column tables, where the columns choose the source, so
// the series are added without one.
// Render with key={widget_id} so the form resets for each widget.
export default function BulkAddSeriesModal({ widget, ctx, onCancel, onSave, hideSource = false }) {
  // Start from the widget's last series: usually you're adding more of the same.
  const last = widget.series[widget.series.length - 1];
  const [source, setSource] = useState(() => sourceKeyOf(last));
  const [tab, setTab] = useState(() => ((hideSource || sourceKeyOf(last)) && last?.tab_name) || undefined);
  const hasSource = hideSource || !!source;
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  const accounts = useMemo(() => {
    const seen = new Set();
    return ctx.getAccountsForTab(tab).filter((a) => {
      const k = accountKey(a);
      return seen.has(k) ? false : seen.add(k);
    });
  }, [ctx, tab]);

  // Accounts this widget already shows from the chosen source (any source
  // when sources are hidden)
  const existing = useMemo(
    () =>
      new Set(
        widget.series
          .filter((r) => (hideSource || (source && sourceKeyOf(r) === source)) && toId(r.account_id) !== -1)
          .map(accountKey)
      ),
    [widget.series, source, hideSource]
  );

  const accountOptions = useMemo(
    () =>
      accounts.map((a) => ({
        value: accountKey(a),
        label: a.account_number ? `${a.account_number} ${a.name}` : a.name,
        disabled: existing.has(accountKey(a)),
      })),
    [accounts, existing]
  );

  const selectable = accountOptions.filter((o) => !o.disabled).map((o) => o.value);
  const toAdd = selected.filter((k) => !existing.has(k));

  const save = async () => {
    const byKey = new Map(accounts.map((a) => [accountKey(a), a]));
    const rows = toAdd.map((k) => {
      const a = byKey.get(k);
      return {
        ...EMPTY_SERIES_FIELDS,
        ...(hideSource ? {} : sourceFields(source)),
        tab_name: tab,
        account_id: toId(a.account_id),
        associated_account_id: toId(a.associated_account_id),
        account_number: a.account_number ?? "",
      };
    });
    setSaving(true);
    try {
      await onSave(widget, rows);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title="Add accounts"
      okText={toAdd.length > 0 ? `Add ${toAdd.length} account${toAdd.length === 1 ? "" : "s"}` : "Add"}
      okButtonProps={{ disabled: toAdd.length === 0 }}
      confirmLoading={saving}
      onCancel={onCancel}
      onOk={save}
    >
      {!hideSource && (
        <>
          <Label>Business case or scenario</Label>
          <Select
            showSearch
            style={{ width: "100%" }}
            placeholder="Business case or scenario"
            value={source}
            options={ctx.sourceOptions}
            filterOption={filterByLabel}
            onChange={(value) => {
              setSource(value);
              setSelected([]);
            }}
          />
        </>
      )}

      <Label>Tab</Label>
      <Select
        style={{ width: "100%" }}
        placeholder="Tab"
        disabled={!hasSource}
        value={tab}
        options={ctx.tabOptions}
        onChange={(value) => {
          setTab(value);
          setSelected([]);
        }}
      />

      <Label>Accounts</Label>
      <Select
        mode="multiple"
        allowClear
        showSearch
        maxTagCount="responsive"
        style={{ width: "100%" }}
        placeholder="Select accounts"
        disabled={!hasSource || !tab}
        value={selected}
        options={accountOptions}
        filterOption={filterByLabel}
        onChange={setSelected}
      />
      <Space size={4} style={{ marginTop: 6 }}>
        <Button size="small" disabled={!tab || selectable.length === 0} onClick={() => setSelected(selectable)}>
          Select all ({selectable.length})
        </Button>
        <Button size="small" disabled={selected.length === 0} onClick={() => setSelected([])}>
          Clear
        </Button>
      </Space>
      {existing.size > 0 && tab && (
        <div style={{ marginTop: 8, fontSize: 12, opacity: 0.65 }}>
          {hideSource
            ? "Accounts already in this table are greyed out."
            : "Accounts already in this chart from the same source are greyed out."}
        </div>
      )}
    </Modal>
  );
}
